<script setup lang="ts">
import { computed } from 'vue'

import { cellKey, injectLayoutDrag, resolveActiveTab } from '../../composables/useLayoutDrag'
import type { DashboardGridAncestor, DashboardWidgetTarget } from '../../models/builder.types'
import type { DashboardCell, DashboardRow } from '../../models/layout.types'
import type { WidgetDataSource, WidgetSheet } from '../../models/widgetExport.types'
import { isWidgetBuilderEditable } from '../../service/widgetSpec'

// Mutual recursion with DashboardGrid; both resolve at render time, so the import cycle is inert.
import DashboardGrid from './DashboardGrid.vue'
import DashboardWidgetHost from './DashboardWidgetHost.vue'
import GridBlockActions from './GridBlockActions.vue'
import WidgetTabs from './WidgetTabs.vue'

import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui'

defineOptions({ name: 'GridCell' })

/**
 * One cell, drawn by depth the way v3 drew it (`CustomDashboardSection.vue:36-196`):
 *
 * - **Root** — a tab group. A folder-tab strip (the section's grip, the tabs, add, the section's
 *   delete) over a white panel holding the active tab's grid. Outside edit mode the strip only
 *   appears when there is a choice to make, i.e. more than one tab.
 * - **Nested** — a block. The widget, under a Move / Duplicate / Delete / Edit overlay. v3 gave
 *   every nested col a default "Tab" and never drew it; a lone tab stays hidden here too, and a
 *   strip appears only for legacy data carrying several.
 */
const props = defineProps<{
  row: DashboardRow
  cell: DashboardCell
  path: readonly DashboardGridAncestor[]
  editMode: boolean
  readOnly: boolean
  wsId: number | string | null
  accountWorkspaces: readonly (number | string)[]
  tabName: string
}>()
const emit = defineEmits<{
  'select-tab': [tabId: string]
  'add-tab': []
  'rename-tab': [payload: { tabId: string; name: string }]
  'duplicate-tab': [tabId: string]
  'remove-tab': [tabId: string]
  'update-tab-rows': [tabId: string, rows: DashboardRow[]]
  'request-widget': []
  'nested-request-widget': [target: DashboardWidgetTarget]
  'row-drag-start': [event: DragEvent]
  'row-drag-end': []
  duplicate: []
  'remove-cell': []
  'remove-row': []
}>()
const { t } = useI18n()
const drag = injectLayoutDrag()

const nested = computed(() => props.path.length > 0)
const editable = computed(() => props.editMode && !props.readOnly)
const activeId = computed(() =>
  drag
    ? resolveActiveTab(
        drag.activeTabs,
        cellKey(props.path, props.row.id, props.cell.id),
        props.cell.tabs,
      )
    : (props.cell.tabs[0]?.id ?? ''),
)
const activeTab = computed(() => props.cell.tabs.find((tab) => tab.id === activeId.value) ?? null)
const showsStrip = computed(() =>
  nested.value ? props.cell.tabs.length > 1 : editable.value || props.cell.tabs.length > 1,
)
/** A nested cell's lone default tab is drawn only if someone actually put sections in it. */
const showsTabGrid = computed(
  () =>
    activeTab.value !== null &&
    (!nested.value || props.cell.tabs.length > 1 || activeTab.value.rows.length > 0),
)

const sources = new Map<string, WidgetDataSource>()

function collect(key: string, instance: unknown): void {
  if (instance) sources.set(key, instance as WidgetDataSource)
  else sources.delete(key)
}

/** §8 — this cell's widget and its tab grids; v3 walked the same set by index arithmetic. */
async function downloadData(): Promise<WidgetSheet[]> {
  const settled = await Promise.allSettled([...sources.values()].map((s) => s.downloadData()))
  return settled.flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
}

defineExpose({ downloadData })
</script>

<template>
  <div
    class="grid-cell group/cell relative flex min-w-0 flex-col"
    :class="{ 'min-h-40': nested && editable }"
    :data-cell-id="cell.id"
  >
    <WidgetTabs
      v-if="showsStrip"
      :tabs="cell.tabs"
      :active-id="activeId"
      :edit-mode="editable"
      :variant="nested ? 'inline' : 'group'"
      :location="{ ancestors: path, rowId: row.id, cellId: cell.id }"
      @select="emit('select-tab', $event)"
      @add="emit('add-tab')"
      @rename="emit('rename-tab', $event)"
      @duplicate="emit('duplicate-tab', $event)"
      @remove="emit('remove-tab', $event)"
    >
      <template v-if="editable && !nested" #leading>
        <span
          class="grid-cell__grip inline-flex h-11 w-12 shrink-0 cursor-grab items-center justify-center rounded-t-lg bg-surface text-accent shadow-sm transition-colors hover:bg-surface-sunken active:cursor-grabbing"
          draggable="true"
          role="button"
          tabindex="-1"
          :aria-label="t('dashboards.grid.dragSection')"
          :title="t('dashboards.grid.dragSection')"
          :data-testid="`dashboard-drag-section-${row.id}`"
          @dragstart="emit('row-drag-start', $event)"
          @dragend="emit('row-drag-end')"
        >
          <Icon name="grip-vertical" :size="16" />
        </span>
      </template>
      <template v-if="editable && !nested" #trailing>
        <button
          type="button"
          class="grid-cell__remove ml-auto inline-flex h-11 w-12 shrink-0 cursor-pointer items-center justify-center rounded-t-lg border-0 bg-surface text-accent shadow-sm transition-colors hover:bg-surface-sunken hover:text-danger"
          :aria-label="t('dashboards.grid.removeSection')"
          :title="t('dashboards.grid.removeSection')"
          data-testid="dashboard-remove-section"
          @click="emit('remove-row')"
        >
          <Icon name="trash" :size="15" />
        </button>
      </template>
    </WidgetTabs>

    <!-- `isolate`: a widget's own z-indexed chrome (toolbar selects) must stay under the overlay. -->
    <div
      class="grid-cell__body isolate flex min-w-0 grow flex-col"
      :class="
        nested
          ? { 'pt-sm': showsStrip }
          : ['rounded-lg bg-surface p-sm shadow-card', { 'rounded-tl-none': showsStrip }]
      "
    >
      <DashboardWidgetHost
        v-if="cell.widget"
        :ref="(instance) => collect('widget', instance)"
        :widget="cell.widget"
        :ws-id="wsId"
        :account-workspaces="accountWorkspaces"
        :read-only="readOnly"
        :tab-name="tabName"
        :section-name="row.name ?? ''"
      />
      <DashboardGrid
        v-if="showsTabGrid && activeTab"
        :key="activeTab.id"
        :ref="(instance) => collect(`tab:${activeTab!.id}`, instance)"
        :rows="activeTab.rows"
        :tab-name="activeTab.name"
        :edit-mode="editMode"
        :read-only="readOnly"
        :ws-id="wsId"
        :account-workspaces="accountWorkspaces"
        :parent-cell-count="row.cells.length"
        :path="[...path, { rowId: row.id, cellId: cell.id, tabId: activeTab.id }]"
        @update:rows="emit('update-tab-rows', activeTab!.id, $event)"
        @request-widget="emit('nested-request-widget', $event)"
      />
      <button
        v-else-if="!cell.widget && !nested && editable"
        type="button"
        class="grid-cell__empty grid min-h-36 w-full cursor-pointer place-items-center content-center gap-xs rounded-md border border-dashed border-border-strong bg-surface-sunken font-sans text-sm text-muted transition-colors hover:border-primary hover:text-primary"
        data-testid="dashboard-add-widget"
        @click="emit('request-widget')"
      >
        <Icon name="plus" :size="15" />
        {{ t('dashboards.grid.addBlock') }}
        <span class="text-xs text-subtle">{{ t('dashboards.grid.emptyGroup') }}</span>
      </button>
      <div
        v-else-if="!cell.widget && !nested"
        class="grid-cell__empty grid min-h-36 place-items-center text-sm text-muted"
      >
        {{ t('dashboards.grid.emptyBlock') }}
      </div>
    </div>

    <GridBlockActions
      v-if="nested && editable"
      :row-id="row.id"
      :empty="!cell.widget"
      :editable="!cell.widget || isWidgetBuilderEditable(cell.widget.kind)"
      @move-start="emit('row-drag-start', $event)"
      @move-end="emit('row-drag-end')"
      @duplicate="emit('duplicate')"
      @remove="emit('remove-cell')"
      @edit="emit('request-widget')"
    />
  </div>
</template>
